import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import round from 'lodash/round';
import { useParams } from 'next/navigation';
import { useClient } from 'urql';

import useEvent from '@lyearn/core/hooks/useEvent';
import { Trans } from '@lyearn/i18n';
import { LargeActionMenuItemType } from '@lyearn/molecules';
import { Typography, useSnackbar } from '@lyearn/ui';

import { useForceCompleteContentProgressMutation } from '../../../../../../../../../learner/views/LearnerProfile/graphql/mutations/generated/forceCompleteContentProgress';
import { useSyncUserContentProgressMutation } from '../../../../../../graphql/mutations/generated/syncUserContentProgress';
import { ContentMaxProgressLearnerDocument } from '../../../../../../graphql/queries/generated/contentMaxProgressLearner';

export enum ViewProgressActionType {
  ForceComplete = 'FORCE_COMPLETE',
  SyncProgress = 'SYNC_PROGRESS',
}

type Props = {
  userId: string;
  userName: string;
  entityId: string;
  showForceCompleteAlert: () => void;
  showSyncProgressAlert: () => void;
  closeForceCompleteAlert: () => void;
  closeSyncProgressAlert: () => void;
  batchId: string;
};

const useJourneyViewProgressActions = (props: Props) => {
  const {
    userId,
    userName,
    entityId,
    showForceCompleteAlert,
    showSyncProgressAlert,
    closeForceCompleteAlert,
    closeSyncProgressAlert,
    batchId,
  } = props;

  const { journeyId } = useParams<{ journeyId: string }>();

  const [maxProgress, setMaxProgress] = useState<number | undefined>();
  const [syncToUserId, setSyncToUserId] = useState<string>('');
  const { t } = useTranslation(['Journey']);
  const { showErrorSnackbar, showSnackbar } = useSnackbar();

  const [_, executeForceComplete] = useForceCompleteContentProgressMutation();
  const [{ fetching }, executeSyncProgress] = useSyncUserContentProgressMutation();

  const syncUserContentProgress = useCallback(async () => {
    const res = await executeSyncProgress({
      input: {
        entityId,
        syncToUserId,
        syncUserIds: [userId],
      },
    });

    if (res?.data?.syncUserContentProgress?.success) {
      showSnackbar({
        message: (
          <Trans
            components={{
              strong: <Typography className="text-text-white" variant="label-02" />,
            }}
            i18nKey="Journey:SyncProgressSuccess"
            values={{
              name: userName,
              progress: round(maxProgress || 0, 1),
            }}
          />
        ),
      });
      closeSyncProgressAlert();
    } else {
      showErrorSnackbar({
        message: t('Common:Error.SomethingWentWrong'),
      });
    }
  }, [
    closeSyncProgressAlert,
    entityId,
    executeSyncProgress,
    maxProgress,
    showErrorSnackbar,
    showSnackbar,
    syncToUserId,
    t,
    userId,
    userName,
  ]);

  const forceCompleteContent = useCallback(async () => {
    const res = await executeForceComplete({
      input: {
        userIds: [userId],
        entityIds: [entityId],
      },
    });

    if (res?.data?.forceCompleteContentProgress?.success) {
      showSnackbar({
        message: (
          <Trans
            components={{
              strong: <Typography className="text-text-white" variant="label-02" />,
            }}
            i18nKey="Journey:ForceCompleteSuccess"
            values={{
              name: userName,
            }}
          />
        ),
      });
      closeForceCompleteAlert();
    } else {
      showErrorSnackbar({
        message: t('Common:Error.SomethingWentWrong'),
      });
    }
  }, [
    closeForceCompleteAlert,
    entityId,
    executeForceComplete,
    showErrorSnackbar,
    showSnackbar,
    t,
    userId,
    userName,
  ]);

  const client = useClient();

  const items: LargeActionMenuItemType[] = useMemo(
    () => [
      {
        id: ViewProgressActionType.SyncProgress,
        label: t('ExternalContent:Buttons.SyncProgress'),
        description: t('Journey:SyncProgressDescription'),
      },
      {
        id: ViewProgressActionType.ForceComplete,
        label: t('Common:Actions.ForceComplete'),
        description: t('Journey:ForceCompleteDescription'),
      },
    ],
    [t],
  );

  const onAction = useEvent(async (actionType: string) => {
    switch (actionType) {
      case ViewProgressActionType.ForceComplete:
        {
          showForceCompleteAlert();
        }
        break;
      case ViewProgressActionType.SyncProgress:
        {
          const { data } = await client
            .query(
              ContentMaxProgressLearnerDocument,
              {
                input: {
                  batchId,
                  contentId: entityId,
                  journeyId: journeyId,
                },
              },
              { requestPolicy: 'network-only' },
            )
            .toPromise();

          if (data?.contentMaxProgressLearner?.performance) {
            setMaxProgress(data.contentMaxProgressLearner.performance.progress);
            setSyncToUserId(data.contentMaxProgressLearner.performance.user.id);
          }
          showSyncProgressAlert();
        }
        break;
    }
  });

  return {
    items,
    onAction,
    forceCompleteContent,
    syncUserContentProgress,
    maxProgress: round(maxProgress || 0, 1),
  };
};

export default useJourneyViewProgressActions;
